"use client";

import React, { createRef, useRef, type ComponentProps } from "react";
import { type DeepReadonly } from "@/utils/tsmagic";
import { Player, type PlayerRef } from "@remotion/player";
import { proxy, useSnapshot } from "valtio";

import { type WrapVideo } from "@matan/database";

import { type EditorSurface } from "./core/boundsDebug";
import { type ReadOnlyCardObjects } from "./core/derivedSchema";
import { usePauseMediaWhileHeld } from "./core/pauseMediaWhileHeld";
import RootComposition from "./RootComposition";

type Props = Partial<ComponentProps<typeof Player>> & {
  readonly wrapVideo: DeepReadonly<WrapVideo>;
  readonly cardObjects: ReadOnlyCardObjects;
  withEndplate?: boolean;
  /**
   * Playback mode props. The player runs in one of two modes:
   *
   * EDITOR MODE (artist + consumer editors) - someone is composing a card, so
   * playback must never start by itself: pass `clickToPlay={false}` and
   * `spaceKeyToPlayOrPause={false}`, and leave `autoPlay` unset, which leaves
   * the play button as the only way to start. (Remotion's space-bar shortcut
   * moves focus to the play button whenever playback starts or stops, so the
   * next space typed in the text box would otherwise toggle playback.)
   *
   * VIEWER MODE (/view + /preview) - someone is being shown a finished card:
   * pass `autoPlay` with `initiallyMuted`. Browsers block autoplay with sound,
   * so muted autoplay is the only form that reliably starts.
   *
   * All three are forwarded to Remotion untouched, and stay `undefined` when
   * omitted so Remotion's own defaults apply. Remotion resolves an undefined
   * `clickToPlay` to `Boolean(controls)`, so a surface that passes `controls`
   * gets click-to-play switched ON unless it opts out explicitly.
   */
  clickToPlay?: boolean;
  autoPlay?: boolean;
  initiallyMuted?: boolean;
  /**
   * Enable IP protection features:
   * - Disables download button in video controls
   * - Disables remote playback and picture-in-picture
   * - Prevents right-click context menu on video
   * Use for consumer-facing modes (view, preview, consumer editor)
   */
  disableDownload?: boolean;
  /**
   * Editor-only debug overlay: outline each text object's wrap box
   * (blue in-bounds / amber content-overflow / red box-out), and on the artist
   * surface consumer-editable text in green. Pass "artist" or "consumer" from
   * the respective editors; leave unset for /view, /preview and Lambda.
   */
  boundsSurface?: EditorSurface;
  /**
   * Viewer surfaces (/view, /preview): draw the purring cat over the card while
   * its media load, after 300ms. Passed into the composition, not to Remotion's
   * Player. See core/BufferingCatOverlay.
   */
  showLoadingCat?: boolean;
};

export const RemotionPlayerRef = proxy(createRef<PlayerRef>());
export const usePlayerRef = () => useSnapshot(RemotionPlayerRef);

const RemotionPlayer = ({
  wrapVideo,
  cardObjects,
  withEndplate,
  disableDownload = false,
  boundsSurface = "off",
  showLoadingCat,
  clickToPlay,
  autoPlay,
  initiallyMuted,
  ...props
}: Props) => {
  const durationInFrames = withEndplate
    ? wrapVideo.durationInFrames + 60
    : wrapVideo.durationInFrames;

  // While playback is held, hold the card's video and audio too (Remotion
  // 4.0.150 leaves them running, then jumps them back). Scoped to our wrapper,
  // which also contains Remotion's shared <Audio> tags.
  const mediaScopeRef = useRef<HTMLDivElement>(null);
  usePauseMediaWhileHeld(RemotionPlayerRef, mediaScopeRef);

  const inputProps = React.useMemo(() => {
    return {
      cardId: undefined,
      data: {
        cardObjects,
        wrapVideo,
      },
      withEndplate,
      boundsSurface,
      loadingCat: showLoadingCat,
    };
  }, [cardObjects, withEndplate, wrapVideo, boundsSurface, showLoadingCat]);

  // IP Protection: Disable download and restrict video access
  const playerProps = disableDownload ? {
    ...props,
    // Disable download button in HTML5 video controls
    controlsList: 'nodownload nofullscreen noremoteplayback',
    // Disable picture-in-picture mode
    disablePictureInPicture: true,
    // Disable remote playback (Chromecast, AirPlay, etc.)
    disableRemotePlayback: true,
  } : props;

  return (
    <div
      ref={mediaScopeRef}
      className={disableDownload ? 'select-none' : undefined}
      onContextMenu={disableDownload ? (e) => e.preventDefault() : undefined}
    >
      <Player
        {...playerProps}
        ref={RemotionPlayerRef}
        inputProps={inputProps}
        component={RootComposition}
        durationInFrames={durationInFrames}
        compositionWidth={wrapVideo.resolutionX}
        compositionHeight={wrapVideo.resolutionY}
        fps={wrapVideo.fps}
        className="max-h-full rounded-lg bg-white shadow-sm lg:grow"
        moveToBeginningWhenEnded={false}
        clickToPlay={clickToPlay}
        autoPlay={autoPlay}
        initiallyMuted={initiallyMuted}
      />
    </div>
  );
};

export const RemotionPlayerPreview = ({
  wrapVideo,
  cardObjects,
  style,
  autoPlay,
  controls,
  loop,
  disableDownload = false,
}: Props) => {
  const inputProps = React.useMemo(() => {
    return {
      cardId: undefined,
      data: {
        cardObjects,
        wrapVideo,
      },
    };
  }, [cardObjects, wrapVideo]);

  const protectionProps = disableDownload
    ? {
        controlsList: "nodownload nofullscreen noremoteplayback" as const,
        disablePictureInPicture: true,
        disableRemotePlayback: true,
      }
    : {};

  return (
    <div
      className={disableDownload ? "h-full w-full select-none" : "h-full w-full"}
      onContextMenu={
        disableDownload ? (e) => e.preventDefault() : undefined
      }
    >
      <Player
        inputProps={inputProps}
        initiallyMuted
        component={RootComposition}
        durationInFrames={wrapVideo.durationInFrames}
        compositionWidth={wrapVideo.resolutionX}
        compositionHeight={wrapVideo.resolutionY}
        fps={wrapVideo.fps}
        className="max-h-full rounded-lg bg-white shadow-sm lg:grow"
        style={style}
        loop={loop}
        moveToBeginningWhenEnded={false}
        autoPlay={autoPlay}
        controls={controls}
        {...protectionProps}
      />
    </div>
  );
};

export default RemotionPlayer;
